import React, { useState, useRef } from 'react';
import { Modal, Header, Button, Progress, Message, Icon, Divider } from 'semantic-ui-react';
import axiosInstance from '../lib/auth/axiosApi';

function UploadVideoModal({ open, onClose, onUploadSuccess }) {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setError(null);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        try {
            setError(null);
            setUploading(true);
            setUploadProgress(0);
            
            // Step 1: Create upload URL
            const urlResponse = await axiosInstance.post('/dijon-backend/api/mux/create_upload/');
            
            if (!urlResponse.data.success) {
                throw new Error(urlResponse.data.error || 'Failed to create upload URL');
            }

            const uploadUrl = urlResponse.data.upload_url;
            
            // Step 2: Upload file to MUX
            const formData = new FormData();
            formData.append('file', selectedFile);

            const xhr = new XMLHttpRequest();
            
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const progress = (event.loaded / event.total) * 100;
                    setUploadProgress(progress);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    setUploading(false);
                    setUploadProgress(100);
                    setSuccess(true);
                    
                    if (onUploadSuccess) {
                        onUploadSuccess({ status: 'success' });
                    }
                    
                    setTimeout(() => {
                        handleClose();
                    }, 2000);
                } else {
                    setUploading(false);
                    setError('Upload failed: ' + xhr.statusText);
                }
            };

            xhr.onerror = () => {
                setUploading(false);
                setError('Upload failed: Network error');
            };

            xhr.open('PUT', uploadUrl);
            xhr.send(selectedFile);

        } catch (err) {
            console.error('Error uploading file:', err);
            setError(err.response?.data?.error || err.message || 'Upload failed');
            setUploading(false);
        }
    };


    const handleClose = () => {
        setUploading(false);
        setUploadProgress(0);
        setError(null);
        setSuccess(false);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="small"
            closeIcon
        >
            <Header icon>
                <Icon name="cloud upload" />
                Upload Video
            </Header>
            
            <Modal.Content>
                {error && (
                    <Message negative>
                        <Message.Header>Upload Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}
                
                {success && (
                    <Message positive>
                        <Message.Header>Upload Successful!</Message.Header>
                        <p>Your video has been uploaded successfully and is being processed.</p>
                    </Message>
                )}
                
                {!success && (
                    <div>
                        <p>Select a video file to upload to your media library.</p>
                        
                        <div style={{ 
                            border: '2px dashed #ddd', 
                            borderRadius: '8px', 
                            padding: '40px', 
                            textAlign: 'center',
                            backgroundColor: '#fafafa',
                            minHeight: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Icon name="cloud upload" size="huge" color="grey" />
                            <div style={{ marginTop: '20px' }}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="video/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <Button
                                    primary
                                    icon="upload"
                                    content={selectedFile ? `Selected: ${selectedFile.name}` : 'Choose Video File'}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                />
                            </div>
                            {selectedFile && !uploading && (
                                <div style={{ marginTop: '15px' }}>
                                    <Button
                                        color="green"
                                        icon="play"
                                        content="Upload Video"
                                        onClick={handleUpload}
                                    />
                                </div>
                            )}
                        </div>
                        
                        {uploading && (
                            <div style={{ marginTop: '20px' }}>
                                <Progress 
                                    percent={uploadProgress} 
                                    indicating 
                                    color="blue"
                                    size="medium"
                                >
                                    Uploading... {Math.round(uploadProgress)}%
                                </Progress>
                            </div>
                        )}
                        
                        <Divider />
                        
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                            <Icon name="info circle" />
                            <strong>Supported formats:</strong> MP4, MOV, AVI, WMV, MKV<br />
                            <strong>Max file size:</strong> 5GB<br />
                            <strong>Processing:</strong> Videos are automatically optimized for web playback
                        </div>
                    </div>
                )}
            </Modal.Content>
            
            <Modal.Actions>
                <Button onClick={handleClose}>
                    {success ? 'Done' : 'Cancel'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default UploadVideoModal;